import { Alert, Button, Select, Space, Typography } from 'antd';

const { Paragraph, Text } = Typography;

/*
 * Bascule de la sélection de séries : retire un code et les épisodes qu'il débloquait, puis
 * pose le nouveau. Indépendante de la transition d'année.
 */
const SeriesPanel = ({
  askConfirm,
  autoTargeted,
  codeOptions,
  codePlatforms,
  job,
  knownPlatforms,
  launch,
  newCode,
  oldCode,
  running,
  setCodePlatforms,
  setNewCode,
  setOldCode,
  simulationOk,
  targetedPlatforms,
}) => (
  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
    <Paragraph type="secondary">
      Retire aux enseignants la sélection choisie et les épisodes qu&apos;elle débloquait, puis leur donne la nouvelle. Les codes se
      créent dans Manage &gt; Codes Webapp. Rejouable : relancé, il ne fait plus rien. Un épisode repris par la nouvelle sélection,
      ou couvert par un autre code encore détenu, n&apos;est jamais retiré.
    </Paragraph>

    <Space wrap align="start">
      <Space direction="vertical" size={2}>
        <Text type="secondary">Code à retirer</Text>
        <Select
          allowClear
          showSearch
          disabled={running}
          onChange={value => setOldCode(value ?? null)}
          options={codeOptions}
          placeholder="Aucun retrait"
          style={{ width: 340 }}
          value={oldCode}
        />
      </Space>
      <Space direction="vertical" size={2}>
        <Text type="secondary">Code à poser</Text>
        <Select
          showSearch
          disabled={running}
          onChange={value => setNewCode(value ?? null)}
          options={codeOptions.filter(o => o.value !== oldCode)}
          placeholder="Choisir un code"
          style={{ width: 340 }}
          value={newCode}
        />
      </Space>
    </Space>

    {!oldCode && (
      <Text type="secondary">
        Sans code à retirer, rien n&apos;est supprimé : les enseignants reçoivent simplement le code choisi.
      </Text>
    )}

    <Space direction="vertical" size={2} style={{ width: '100%' }}>
      <Text type="secondary">Plateformes</Text>
      <Select
        allowClear
        mode="multiple"
        disabled={running}
        onChange={value => setCodePlatforms(value && value.length > 0 ? value : null)}
        options={knownPlatforms.map(p => ({
          label: autoTargeted.includes(p) ? `${p} (configurée avec ce code)` : p,
          value: p,
        }))}
        placeholder="Toutes celles configurées avec ce code"
        style={{ maxWidth: 700, width: '100%' }}
        value={codePlatforms || []}
      />
      <Text type="secondary">
        Laisser vide pour suivre la configuration. Choisir une ou plusieurs plateformes permet de n&apos;en basculer qu&apos;une
        partie — pour vérifier sur un périmètre réduit, ou pour changer le code d&apos;une seule plateforme parmi celles qui le
        partagent.
      </Text>
    </Space>

    {codePlatforms && codePlatforms.some(p => !autoTargeted.includes(p)) && (
      <Alert
        type="warning"
        showIcon
        message="Certaines plateformes choisies ne sont pas configurées avec ce code"
        description={`${codePlatforms
          .filter(p => !autoTargeted.includes(p))
          .join(
            ', ',
          )} : les enseignants existants recevront ${newCode}, mais les nouveaux comptes continueront de recevoir le code de leur configuration. Mettre à jour la configuration pour aligner les deux.`}
      />
    )}

    {newCode &&
      (targetedPlatforms.length > 0 ? (
        <Text type="secondary">
          Plateformes visées : <Text strong>{targetedPlatforms.join(', ')}</Text>
        </Text>
      ) : (
        <Alert
          type="warning"
          showIcon
          message={`Aucune plateforme n'est configurée avec ${newCode}`}
          description="Renseigner ce code dans la configuration, ou choisir explicitement des plateformes."
        />
      ))}

    <Space>
      <Button disabled={running || !newCode} loading={running && job.dryRun} onClick={() => launch(true)}>
        Simuler
      </Button>
      <Button
        danger
        type="primary"
        disabled={!simulationOk || running}
        loading={running && !job.dryRun}
        onClick={() =>
          askConfirm({
            description: oldCode
              ? `Les enseignants perdront les séries de ${oldCode} qui ne sont pas reprises, et recevront celles de ${newCode}.`
              : `Les enseignants recevront les séries de ${newCode}. Aucun code n'est retiré.`,
            expected: newCode,
            kind: 'code',
            title: 'Confirmer la bascule de sélection',
          })
        }>
        Basculer la sélection
      </Button>
      {!simulationOk && newCode && <Text type="secondary">Lancer d&apos;abord une simulation.</Text>}
    </Space>
  </Space>
);

export default SeriesPanel;
